import { useCallback, useContext, useState } from 'react';
import styled from '@emotion/styled';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import type { Chat } from '@trinity/chat';
import { ChatActionType } from '@trinity/chat';
import { SegmentChatContext } from '@trinity/chat-components/components/BrandInsights/SegmentChat.Context';
import { SubmitActionContext } from '@trinity/chat-components/components/Chat/context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { useNavigate } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { CancelAction } from '../presentation/Message.QueryHint/CancelAction';
import { AnalysisQueryHint } from '../presentation/Message.QueryHint/QueryHint';
import { useCancelSegmentChat } from './hooks/useCancelSegmentChat';

type Props = {
  queryIdentifier: string;
  value: Chat.QueryState.SegmentChatNeeded;
};

export const SegmentChatSelection = (props: Props) => {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const navigate = useNavigate();
  const { setSegmentChatStatus, setSegmentIconUrl, setSegmentImmutableTag, segmentIconsCache } =
    useContext(SegmentChatContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const submitAction = useContext(SubmitActionContext);

  const cancelMutation = useCancelSegmentChat({ queryIdentifier: props.queryIdentifier });

  const handleCheckboxChange = useCallback((id: number, checked: boolean) => {
    setSelectedId(checked ? id : null);
  }, []);

  const handleLaunchDigitalTwin = useCallback(() => {
    if (selectedId === null || !props.value.projectSlug) return;
    navigate(`/projects/${props.value.projectSlug}/hcp-digital-twins?segmentId=${selectedId}`);
  }, [selectedId, props.value.projectSlug, navigate]);

  const handleBrowseAll = useCallback(() => {
    if (!props.value.projectSlug) return;
    navigate(`/projects/${props.value.projectSlug}/hcp-digital-twins`);
  }, [props.value.projectSlug, navigate]);

  const handleCancel = useCallback(() => {
    return cancelMutation.mutateAsync();
  }, [cancelMutation]);

  const selectedSegment = props.value.segments.find((s) => s.id === selectedId);
  const segmentSelected = selectedId !== null;
  const canActOnSegment = segmentSelected && !!props.value.projectSlug && !cancelMutation.isPending;
  const canBrowse = !!props.value.projectSlug && !segmentSelected;

  const handleChatWithSegment = useCallback(() => {
    if (selectedId === null || !selectedSegment) return;
    const groupTag = selectedSegment.groupTag;

    const resolvedIconUrl =
      segmentIconsCache[selectedSegment.identifier] ?? selectedSegment.iconUrl ?? undefined;

    // Always set status and icon, regardless of whether a groupTag is linked
    setSegmentChatStatus(true);
    setSegmentIconUrl(resolvedIconUrl);

    if (!groupTag) {
      // No linked group tag — clear any existing tags and start chat without persona injection
      setSegmentImmutableTag(null);
      dispatch({ type: 'filters/updated', payload: { filters: { tags: [] } } });
      submitAction?.(
        {
          type: ChatActionType.SelectSegmentForChat,
          segmentIdentifier: selectedSegment.identifier,
          segmentId: selectedSegment.id,
          groupTagId: 0,
          groupTagName: '',
          iconUrl: resolvedIconUrl,
        },
        `Chat with ${selectedSegment.name}`
      );
      return;
    }

    setSegmentImmutableTag({
      id: groupTag.id,
      name: groupTag.name,
      segmentIdentifier: selectedSegment.identifier,
    });
    dispatch({
      type: 'filters/updated',
      payload: {
        filters: {
          tags: [
            {
              id: groupTag.id,
              name: groupTag.name,
              metadata: { canRemove: false, segmentIdentifier: selectedSegment.identifier },
            },
          ],
        },
      },
    });
    submitAction?.(
      {
        type: ChatActionType.SelectSegmentForChat,
        segmentIdentifier: selectedSegment.identifier,
        segmentId: selectedSegment.id,
        groupTagId: groupTag.id,
        groupTagName: groupTag.name,
        iconUrl: resolvedIconUrl,
      },
      `Chat with ${selectedSegment.name}`
    );
  }, [
    selectedId,
    selectedSegment,
    segmentIconsCache,
    setSegmentChatStatus,
    setSegmentIconUrl,
    setSegmentImmutableTag,
    dispatch,
    submitAction,
  ]);

  return (
    <Root>
      <HeaderCopy>{props.value.title ?? 'Ok, great! What segment would you like to chat with?'}</HeaderCopy>
      <PickCopy>Select Segment</PickCopy>
      <Segments>
        <FormGroup>
          {props.value.segments.map((segment) => (
            <FormControlLabel
              key={segment.id}
              control={
                <Checkbox
                  checked={selectedId === segment.id}
                  onChange={(_, checked) => handleCheckboxChange(segment.id, checked)}
                />
              }
              label={segment.name}
            />
          ))}
        </FormGroup>
      </Segments>
      <Actions>
        {canActOnSegment && (
          <AnalysisQueryHint value={`Chat with ${selectedSegment!.name}`} onClick={handleChatWithSegment} />
        )}
        {canActOnSegment && (
          <AnalysisQueryHint value="Launch Digital Twin" onClick={handleLaunchDigitalTwin} />
        )}
        {canBrowse && <AnalysisQueryHint value="Browse all segments" onClick={handleBrowseAll} />}
        <CancelAction displayValue="Actually, I'd like to do something else." onClick={handleCancel} />
      </Actions>
    </Root>
  );
};

const Root = styled.div`
  box-sizing: border-box;
`;

const HeaderCopy = styled.div`
  box-sizing: border-box;
  border-bottom: 1px solid ${(props) => props.theme.palette.gray.light1};
  padding-bottom: 10px;
  margin-bottom: 10px;
`;

const PickCopy = styled.div`
  font-size: 15px;
  font-family: ${(props) => props.theme.fonts.semiBold};
  margin-bottom: 10px;
`;

const Segments = styled.div`
  display: flex;
  flex-direction: column;

  &:not(:last-child) {
    margin-bottom: 20px;
  }
`;

const Actions = styled.div`
  margin-top: 10px;

  > :not(:last-child) {
    margin-bottom: 10px;
  }
`;
